// ABOUTME: Compound accordion with multiple collapsible panels built on Radix UI
// ABOUTME: Use for multi-panel accordions with compound component API

import * as React from "react"
import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { ChevronDown } from "lucide-react"
import { cn } from "@shared/lib/utils"

// Base Accordion components
const AccordionRoot = AccordionPrimitive.Root

const AccordionItem = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn("border-b", className)}
    {...props}
  />
))
AccordionItem.displayName = "AccordionItem"

const AccordionTrigger = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & { actions?: React.ReactNode }
>(({ className, children, actions, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex items-center">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex flex-1 items-center justify-between py-4 text-sm font-medium transition-all text-left [&[data-state=open]>svg]:rotate-180",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronDown className="h-4 w-4 shrink-0 text-gray-500 transition-transform duration-200" />
    </AccordionPrimitive.Trigger>
    {/* Beside the toggle, never inside it: the trigger is a <button>, and a button nested in a
        button is invalid HTML that React reports as a nesting error. Controls here also keep
        their clicks to themselves, so they no longer need to stop the toggle from firing. */}
    {actions}
  </AccordionPrimitive.Header>
))
AccordionTrigger.displayName = "AccordionTrigger"

const AccordionContent = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div className={cn("pb-4 pt-0", className)}>{children}</div>
  </AccordionPrimitive.Content>
))
AccordionContent.displayName = "AccordionContent"

// CAccordionCompound component with Flowbite-compatible API
export interface CAccordionCompoundProps {
  alwaysOpen?: boolean
  flush?: boolean
  collapseAll?: boolean
  children?: React.ReactNode
  className?: string
}

interface CAccordionPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  isOpen?: boolean
}

type CAccordionTitleProps = React.PropsWithChildren<React.HTMLAttributes<HTMLButtonElement>> & {
  /** Controls that belong in the header row but not in the toggle itself — buttons, dropdowns. */
  actions?: React.ReactNode
}

type CAccordionContentProps = React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>

// Type for components with static identifier properties
interface ComponentWithAccordionMarker {
  __isAccordionTitle?: boolean
  __isAccordionContent?: boolean
}

type CAccordionCompoundComponent = React.FC<CAccordionCompoundProps> & {
  Panel: React.FC<CAccordionPanelProps>
  Title: React.FC<CAccordionTitleProps>
  Content: React.FC<CAccordionContentProps>
}

const CAccordionCompoundBase: React.FC<CAccordionCompoundProps> = ({
  alwaysOpen,
  flush,
  collapseAll,
  children,
  className,
}) => {
  const childrenArray = React.Children.toArray(children)
  const panels = childrenArray.filter((child): child is React.ReactElement<CAccordionPanelProps> =>
    React.isValidElement(child),
  )

  const renderPanels = () =>
    panels.map((panel, index) => {
      const panelChildren = React.Children.toArray(panel.props.children)
      const title = panelChildren.find(
        (child): child is React.ReactElement<CAccordionTitleProps> =>
          React.isValidElement(child) && (child.type as ComponentWithAccordionMarker).__isAccordionTitle === true,
      )
      const content = panelChildren.find(
        (child): child is React.ReactElement<CAccordionContentProps> =>
          React.isValidElement(child) && (child.type as ComponentWithAccordionMarker).__isAccordionContent === true,
      )

      return (
        <AccordionItem
          key={index}
          value={`item-${index}`}
        >
          <AccordionTrigger
            className={title?.props.className}
            actions={title?.props.actions}
          >
            {title?.props.children}
          </AccordionTrigger>
          <AccordionContent className={content?.props.className}>{content?.props.children}</AccordionContent>
        </AccordionItem>
      )
    })

  if (alwaysOpen) {
    return (
      <AccordionRoot
        type="multiple"
        defaultValue={collapseAll ? [] : panels.map((_, i) => `item-${i}`)}
        className={cn(flush && "border-0 *:border-0", className)}
      >
        {renderPanels()}
      </AccordionRoot>
    )
  }

  return (
    <AccordionRoot
      type="single"
      defaultValue={collapseAll ? undefined : "item-0"}
      collapsible
      className={cn(flush && "border-0 *:border-0", className)}
    >
      {renderPanels()}
    </AccordionRoot>
  )
}

const CAccordionPanel: React.FC<CAccordionPanelProps> = ({ children }) => {
  return <>{children}</>
}

const CAccordionTitle: React.FC<CAccordionTitleProps> & { __isAccordionTitle?: boolean } = ({ children }) => {
  return <>{children}</>
}
CAccordionTitle.displayName = "CAccordionTitle"
CAccordionTitle.__isAccordionTitle = true

const CAccordionContentComp: React.FC<CAccordionContentProps> & { __isAccordionContent?: boolean } = ({ children }) => {
  return <>{children}</>
}
CAccordionContentComp.displayName = "CAccordionContent"
CAccordionContentComp.__isAccordionContent = true

const CAccordionCompoundWithStatics: CAccordionCompoundComponent = Object.assign(CAccordionCompoundBase, {
  Panel: CAccordionPanel,
  Title: CAccordionTitle,
  Content: CAccordionContentComp,
})

// Exported under its own identifier: Fast Refresh (react-refresh/only-export-components)
// recognises a component by the name, and the Object.assign that attaches the statics is opaque to it.
export const CAccordionCompound = CAccordionCompoundWithStatics

// Export base components for potential reuse
export { AccordionRoot, AccordionItem, AccordionTrigger, AccordionContent }
